import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { FileText, ShieldCheck, ClipboardCheck, MessageCircle, Phone, Mail, ArrowRight, Quote, CheckCircle, Sparkles, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const services = [
  {
    icon: FileText,
    title: 'Policies & Procedures',
    desc: 'Creation and implementation of Chain of Responsibility, WHS, fatigue, and driver management policies tailored to your operation — not off-the-shelf templates.',
  },
  {
    icon: ClipboardCheck,
    title: 'Permits',
    desc: 'Oversize/overmass, restricted access vehicle permits, road access approvals, and pilot vehicle notifications — handled end-to-end.',
  },
  {
    icon: ShieldCheck,
    title: 'Clearances',
    desc: 'MSIC, mine site, port, and rail clearances processed and tracked so you never miss a renewal.',
  },
  {
    icon: MessageCircle,
    title: 'General Advice',
    desc: 'Speak to real transport professionals — drivers, ops managers, and compliance officers — about the exact issue in front of you today.',
  },
];

const testimonials = [
  {
    quote: 'Truckn Send It sorted our Chain of Responsibility policy in a week — something we\'d been putting off for 18 months. Clear, practical, and priced fairly.',
    name: 'Mark T.',
    role: 'Operations Manager, NSW-based tipper fleet',
  },
  {
    quote: 'They handled two MSIC applications and an oversize permit for us in the same fortnight. Real people who understand the industry — a rare thing.',
    name: 'Kim R.',
    role: 'Owner-Operator, QLD',
  },
  {
    quote: 'We rang expecting a sales pitch. Instead we got straight answers about our fatigue records and a fix that stood up to an audit two months later.',
    name: 'Dave S.',
    role: 'Fleet Owner, VIC',
  },
];

export default function ComplianceServices() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', company: '', phone: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API}/compliance-enquiries`, form);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong — please try emailing us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#1e3a5f] via-[#254d7f] to-[#0f2340] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-red-500 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-emerald-400 rounded-full blur-3xl" />
        </div>
        <div className="relative container mx-auto px-4 py-14 sm:py-20">
          <span className="inline-block bg-red-600 text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow mb-4">
            Compliance done for you
          </span>
          <h1 className="text-3xl sm:text-5xl font-bold leading-tight mb-4 max-w-3xl">
            We&apos;ll handle the paperwork so you can keep your trucks moving.
          </h1>
          <p className="text-base sm:text-lg text-blue-100 max-w-2xl mb-6">
            From Chain of Responsibility policies to MSIC clearances and restricted-access permits — Truckn Send It compliance experts turn a mountain of red tape into a single conversation.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => document.getElementById('enquiry-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="bg-red-600 hover:bg-red-700 text-white shadow-lg"
              data-testid="compliance-hero-form-cta"
            >
              <Mail className="w-4 h-4 mr-2" /> Send us an enquiry
            </Button>
            <Button
              variant="outline"
              onClick={() => (window.location.href = 'tel:1300380700')}
              className="border-white/60 text-white hover:bg-white hover:text-[#1e3a5f]"
              data-testid="compliance-call-cta"
            >
              <Phone className="w-4 h-4 mr-2" /> Call 1300 380 700
            </Button>
          </div>
        </div>
      </section>

      {/* Services grid */}
      <section className="flex-1 container mx-auto px-4 py-12 sm:py-16">
        <div className="max-w-3xl mx-auto text-center mb-10">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1e3a5f] mb-3">What we can help with</h2>
          <p className="text-gray-600">Four core services, one team of Australian transport professionals.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          {services.map((s) => {
            const Icon = s.icon;
            return (
              <Card
                key={s.title}
                className="p-5 border-l-4 border-l-red-600 hover:shadow-lg transition-shadow"
                data-testid={`compliance-service-${s.title.toLowerCase().replace(/[^a-z]/g, '-')}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1e3a5f] text-lg mb-1">{s.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Testimonials */}
        <div className="mt-14">
          <div className="max-w-3xl mx-auto text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1e3a5f] mb-2">What our clients say</h2>
            <p className="text-gray-600 text-sm">Real feedback from real operators.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {testimonials.map((t, i) => (
              <Card
                key={i}
                className="p-5 relative bg-white hover:shadow-lg transition-shadow"
                data-testid={`compliance-testimonial-${i}`}
              >
                <Quote className="w-8 h-8 text-red-100 absolute top-3 right-3" />
                <p className="text-sm text-gray-700 leading-relaxed italic mb-4">&ldquo;{t.quote}&rdquo;</p>
                <div className="text-xs">
                  <p className="font-bold text-[#1e3a5f]">{t.name}</p>
                  <p className="text-gray-500">{t.role}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Enquiry form */}
        <div className="mt-14" id="enquiry-form">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-[#1e3a5f] mb-2">Tell us what you need</h2>
              <p className="text-gray-600 text-sm">A quick note is enough — we&apos;ll reply the same business day.</p>
            </div>
            <Card className="p-6 sm:p-8 shadow-lg border-t-4 border-t-red-600">
              {submitted ? (
                <div className="text-center py-8" data-testid="compliance-form-success">
                  <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-bold text-[#1e3a5f] mb-1">Thanks — we&apos;ve got it.</h3>
                  <p className="text-sm text-gray-600 mb-4">One of our compliance team will be in touch the same business day.</p>
                  <Button
                    variant="outline"
                    onClick={() => { setSubmitted(false); setForm({ name: '', company: '', phone: '', email: '', message: '' }); }}
                    data-testid="compliance-form-again"
                  >
                    Send another enquiry
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="compliance-enquiry-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="ce-name">Your name *</Label>
                      <Input
                        id="ce-name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Jane Doe"
                        required
                        data-testid="compliance-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="ce-company">Company <span className="text-xs text-gray-400">(optional)</span></Label>
                      <Input
                        id="ce-company"
                        value={form.company}
                        onChange={(e) => setForm({ ...form, company: e.target.value })}
                        placeholder="ACME Transport"
                        data-testid="compliance-company-input"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="ce-phone">Phone *</Label>
                      <Input
                        id="ce-phone"
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="0400 000 000"
                        required
                        data-testid="compliance-phone-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="ce-email">Email <span className="text-xs text-gray-400">(optional)</span></Label>
                      <Input
                        id="ce-email"
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="you@example.com"
                        data-testid="compliance-email-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="ce-message">What do you need help with? *</Label>
                    <Textarea
                      id="ce-message"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="e.g. Two MSIC clearances and a Chain of Responsibility policy for a 6-truck operation."
                      rows={5}
                      required
                      data-testid="compliance-message-input"
                    />
                  </div>
                  {error && <p className="text-sm text-red-600" data-testid="compliance-form-error">{error}</p>}
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-6 text-base shadow"
                    data-testid="compliance-form-submit"
                  >
                    {submitting ? 'Sending…' : 'Send enquiry'}
                    {!submitting && <ArrowRight className="w-4 h-4 ml-2" />}
                  </Button>
                  <p className="text-xs text-gray-500 text-center">
                    Prefer email? <a href="mailto:info@trucknsendit.com" className="underline">info@trucknsendit.com</a> · Prefer to call? <a href="tel:1300380700" className="underline">1300 380 700</a>
                  </p>
                </form>
              )}
            </Card>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-10 rounded-2xl bg-gradient-to-r from-red-600 to-red-500 text-white p-6 sm:p-8 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-xl">
          <div>
            <h3 className="text-xl sm:text-2xl font-bold mb-1">Not sure where to start?</h3>
            <p className="text-red-50 text-sm sm:text-base">Tell us your problem in one email — we&apos;ll come back with a plan the same business day.</p>
          </div>
          <Button
            onClick={() => document.getElementById('enquiry-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            className="bg-white text-red-700 hover:bg-red-50 font-semibold shadow-md"
            data-testid="compliance-bottom-cta"
          >
            Start the conversation <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-sm text-gray-500 hover:text-gray-700 underline"
            data-testid="compliance-back-home"
          >
            ← Back to home
          </button>
        </div>
      </section>

      {/* KPTCC software callout */}
      <section className="bg-white py-10 sm:py-14" data-testid="compliance-kptcc-callout">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto relative rounded-2xl overflow-hidden shadow-xl bg-gradient-to-br from-[#1e3a5f] via-[#254d7f] to-[#0f2340] text-white">
            <div className="absolute -top-16 -right-16 w-56 h-56 bg-red-500/25 rounded-full blur-3xl" aria-hidden />
            <div className="relative p-6 sm:p-9">
              <div className="grid md:grid-cols-[1.4fr_1fr] gap-6 items-center">
                <div>
                  <span className="inline-flex items-center gap-1 bg-red-600 text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow mb-3">
                    <Sparkles className="w-3 h-3" /> Software partner
                  </span>
                  <h3 className="text-xl sm:text-3xl font-bold leading-tight mb-2">
                    Looking for simple, user-friendly compliance software that does it all?
                  </h3>
                  <p className="text-blue-100 text-sm sm:text-base mb-5 max-w-lg">
                    Our partners at <span className="font-semibold text-white">KP Transport Compliance Consulting</span> (KPTCC) build cohesive, easy-to-use compliance tools trusted by Australian operators — everything you need in one place.
                  </p>
                  <a
                    href="https://www.kptcc.com.au"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-white hover:bg-blue-50 text-[#1e3a5f] font-semibold px-5 py-3 rounded-full shadow-lg transition-all hover:shadow-xl"
                    data-testid="compliance-kptcc-cta"
                  >
                    Visit kptcc.com.au
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
                <div className="hidden md:flex justify-center">
                  <a
                    href="https://www.kptcc.com.au"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white rounded-2xl p-5 shadow-xl text-center max-w-[240px] hover:shadow-2xl transition-shadow"
                  >
                    <p className="text-[10px] font-bold uppercase tracking-widest text-red-700 mb-3">
                      Trusted partner
                    </p>
                    <img
                      src="https://customer-assets-wrfwihn1.emergentagent.net/job_a0e7f17d-9159-4d2a-a971-2aaa914eeb6a/artifacts/w4jij5c6_IMG_0026.webp"
                      alt="Kimberley Priest Transport Compliance Consulting"
                      className="mx-auto max-h-20 object-contain"
                    />
                    <p className="mt-3 text-[11px] text-gray-500 leading-relaxed">
                      kptcc.com.au
                    </p>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
